import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PiSparkleLight, PiCheckCircleLight, PiWarningCircleLight } from 'react-icons/pi';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { GenerateMoreProgress } from '../../hooks/useGenerateMore';
import { useTranslation } from 'react-i18next';

interface FindMoreProgressProps {
  progress: GenerateMoreProgress;
  isGenerating: boolean;
}

/**
 * Full-tab centered progress display for "Find More" generation.
 * Replaces tab content while running — similar to ProgressTracker.
 */
const FindMoreProgress: React.FC<FindMoreProgressProps> = ({ progress, isGenerating }) => {
  const { t } = useTranslation();
  const show = isGenerating || progress.status === 'complete' || progress.status === 'failed';

  if (!show) return null;

  const isFailed = progress.status === 'failed';
  const isComplete = progress.status === 'complete';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-start py-16 px-8 min-h-[320px]"
        >
          <div className="w-full max-w-sm">
            {/* Header with icon */}
            <div className="flex items-center justify-start gap-2.5 mb-5">
              {isComplete ? (
                <PiCheckCircleLight className="h-6 w-6 text-green-500" />
              ) : isFailed ? (
                <PiWarningCircleLight className="h-6 w-6 text-red-500" />
              ) : (
                <MiniLoadingToCheckmark isLoading={true} />
              )}
              <h3 className={`text-base font-medium ${
                isFailed
                  ? 'text-red-700 dark:text-red-400'
                  : isComplete
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-gray-900 dark:text-gray-100'
              }`}>
                {isComplete
                  ? t('Generation Complete')
                  : isFailed
                    ? t('Generation Failed')
                    : t('Generating...')}
              </h3>
            </div>

            {/* Progress bar */}
            {!isFailed && (
              <div className="mb-3">
                <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${
                      isComplete
                        ? 'bg-green-500'
                        : 'bg-gradient-to-r from-blue-500 to-blue-900'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(progress.percent, 100)}%` }}
                    transition={{ duration: 0.7, ease: 'easeInOut' }}
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block text-left">
                  {Math.round(progress.percent)}% {t('Complete')}
                </span>
              </div>
            )}

            {/* Current step message */}
            <div className="relative mt-3">
              <div className="absolute left-[9px] top-1 bottom-1 w-px bg-gray-200 dark:bg-gray-700 opacity-50" />
              <div className="pl-7">
                <div className="relative animate-fadePulse">
                  <div className="absolute -left-7 top-0.5 flex items-start justify-start bg-gray-50 dark:bg-gray-900 rounded-full">
                    {isComplete ? (
                      <PiCheckCircleLight className="h-4 w-4 text-green-500" />
                    ) : isFailed ? (
                      <PiWarningCircleLight className="h-4 w-4 text-red-500" />
                    ) : (
                      <PiSparkleLight className="h-4 w-4 text-blue-500 animate-fadePulse" />
                    )}
                  </div>
                  <p className={`text-sm ${
                    isFailed
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {progress.message || t('Processing...')}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer note */}
            {!isComplete && !isFailed && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-6 text-left">
                {t('This may take up to a minute.')}
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FindMoreProgress;
